export const INDIA_GEO_ID = 'IND';

export const GEO_MAP_HINT =
  'Your current state is selected from browser location. Choose a district to zoom further. Field employees and meeting routes overlay on the map.';

export const GEO_BRANCH_QUERY = {
  BOUNDARY_TYPE: 'boundary_type',
  LGD_CODE: 'lgd_boundary_code',
};

export const GEO_BRANCH_TOOLTIP_LABEL = {
  ID: 'ID',
  ADDRESS: 'Address',
};

export const GEO_BRANCH_MARKER = {
  WIDTH: 36,
  HEIGHT: 46,
  ANCHOR_X: 18,
  ANCHOR_Y: 46,
  FILL: '#0c4e8d',
  INNER: '#ffffff',
};

export const GEO_BRANCH_INFO_MAX_WIDTH = 280;

export const GEO_BRANCH_INFO_PIXEL_OFFSET_Y = -6;

export function buildGeoBranchMarkerSvg({
  fill = GEO_BRANCH_MARKER.FILL,
  inner = GEO_BRANCH_MARKER.INNER,
} = {}) {
  return `<svg xmlns="http://www.w3.org/2000/svg" width="${GEO_BRANCH_MARKER.WIDTH}" height="${GEO_BRANCH_MARKER.HEIGHT}" viewBox="0 0 36 46">
    <path fill="${fill}" d="M18 0C8.06 0 0 8.06 0 18c0 13.5 18 28 18 28s18-14.5 18-28C36 8.06 27.94 0 18 0z"/>
    <circle cx="18" cy="17.5" r="11.2" fill="${inner}"/>
    <path fill="${fill}" d="M18 9.4l7.4 3.3v1.2H10.6v-1.2L18 9.4zm-6.6 5.6h2.2v6.4h-2.2zm3.7 0h2.2v6.4h-2.2zm3.6 0h2.2v6.4H18.7zm3.7 0h2.2v6.4h-2.2zM10.6 22.6h14.8v1.7H10.6z"/>
  </svg>`;
}

export function getGeoBranchMarkerDataUri() {
  return `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(buildGeoBranchMarkerSvg())}`;
}

export const GEO_LOCATION_TYPE = {
  STATE: 'STATE',
  DISTRICT: 'DISTRICT',
  SUB_DISTRICT: 'SUB_DISTRICT',
  BLOCK: 'BLOCK',
  VILLAGE: 'VILLAGE',
  CUSTOM: 'CUSTOM',
};

export const GEO_SEARCH_MIN_CHARS = 3;

export const GEO_PARENT_BREADCRUMB_SEPARATOR = ' › ';

export const GEO_API_LEVEL_TO_LOCATION_TYPE = {
  IND_STATE: GEO_LOCATION_TYPE.STATE,
  STATE: GEO_LOCATION_TYPE.STATE,
  DISTRICT: GEO_LOCATION_TYPE.DISTRICT,
  SUB_DIST: GEO_LOCATION_TYPE.SUB_DISTRICT,
  SUB_DISTRICT: GEO_LOCATION_TYPE.SUB_DISTRICT,
  'SUB-DISTRICT': GEO_LOCATION_TYPE.SUB_DISTRICT,
  BLOCK: GEO_LOCATION_TYPE.BLOCK,
  VILLAGE: GEO_LOCATION_TYPE.VILLAGE,
  CUSTOM: GEO_LOCATION_TYPE.CUSTOM,
  CUSTOM_GEO: GEO_LOCATION_TYPE.CUSTOM,
};

export const GEO_LOCATION_TYPE_LABEL = {
  [GEO_LOCATION_TYPE.STATE]: 'State',
  [GEO_LOCATION_TYPE.DISTRICT]: 'District',
  [GEO_LOCATION_TYPE.SUB_DISTRICT]: 'Sub-district',
  [GEO_LOCATION_TYPE.BLOCK]: 'Block',
  [GEO_LOCATION_TYPE.VILLAGE]: 'Village',
  [GEO_LOCATION_TYPE.CUSTOM]: 'Custom',
};

export const GEO_LOCATION_PLACEHOLDER = {
  [GEO_LOCATION_TYPE.STATE]: 'Select state',
  [GEO_LOCATION_TYPE.DISTRICT]: 'Select district',
  [GEO_LOCATION_TYPE.SUB_DISTRICT]: 'Select sub-district',
  [GEO_LOCATION_TYPE.BLOCK]: 'Select block',
  [GEO_LOCATION_TYPE.VILLAGE]: 'Select village',
};

/** Child location type for Cascader lazy-load (village is leaf). */
export const GEO_LOCATION_CHILD_TYPE = {
  [GEO_LOCATION_TYPE.STATE]: GEO_LOCATION_TYPE.DISTRICT,
  [GEO_LOCATION_TYPE.DISTRICT]: GEO_LOCATION_TYPE.SUB_DISTRICT,
  [GEO_LOCATION_TYPE.SUB_DISTRICT]: GEO_LOCATION_TYPE.VILLAGE,
};

export const GEO_LOCATION_HIERARCHY = [
  GEO_LOCATION_TYPE.STATE,
  GEO_LOCATION_TYPE.DISTRICT,
  GEO_LOCATION_TYPE.SUB_DISTRICT,
  GEO_LOCATION_TYPE.VILLAGE,
];

export const GEO_LOCATION_CODE_FIELDS = {
  [GEO_LOCATION_TYPE.STATE]: ['lgd_state_code'],
  [GEO_LOCATION_TYPE.DISTRICT]: ['lgd_district_code'],
  [GEO_LOCATION_TYPE.SUB_DISTRICT]: [
    'lgd_subdistrict_code',
    'lgd_sub_district_code',
    'subdistrict_code',
  ],
  [GEO_LOCATION_TYPE.VILLAGE]: ['lgd_village_code', 'village_code'],
};

export const GEO_LOCATION_GEOMETRY_FIELDS = {
  [GEO_LOCATION_TYPE.STATE]: ['state_location'],
  [GEO_LOCATION_TYPE.DISTRICT]: ['district_location'],
  [GEO_LOCATION_TYPE.SUB_DISTRICT]: ['sub_district_location', 'subdistrict_location'],
  [GEO_LOCATION_TYPE.VILLAGE]: ['village_location'],
};

export const GEO_LOCATION_GEOMETRY_FALLBACK_FIELDS = [
  'boundary_data',
  'location',
  'geometry',
  'geom',
  'wkt',
  'geojson',
];

export const GEO_MAP_FIT_BOUNDS = {
  PADDING: 48,
  MAX_ZOOM: 16,
};

export const GEOGRAPHIC_COORDINATE_LIMIT = 180;

export const INDIA_NSF_LCC = {
  SEMI_MAJOR_AXIS: 6378137,
  FLATTENING: 1 / 298.257223563,
  LATITUDE_OF_ORIGIN: 24,
  CENTRAL_MERIDIAN: 80,
  STANDARD_PARALLEL_1: 12.472944444,
  STANDARD_PARALLEL_2: 35.17280555555556,
  FALSE_EASTING: 4000000,
  FALSE_NORTHING: 4000000,
  INVERSE_ITERATIONS: 12,
};

export const BROWSER_GEOLOCATION_OPTIONS = {
  enableHighAccuracy: false,
  timeout: 10000,
  maximumAge: 300000,
};

export const BROWSER_GEOLOCATION_REFRESH_OPTIONS = {
  enableHighAccuracy: false,
  timeout: 10000,
  maximumAge: 0,
};

export const INDIA_STATE_GEOJSON_NAME_KEYS = ['NAME_1', 'st_nm', 'ST_NM', 'name', 'NAME'];

// Maps Core geo_name values to GeoJSON NAME_1 / VARNAME_1 variants.
export const INDIA_STATE_NAME_ALIASES = {
  'Andaman And Nicobar Islands': ['Andaman and Nicobar'],
  Odisha: ['Orissa'],
  'The Dadra And Nagar Haveli And Daman And Diu': [
    'Dadra and Nagar Haveli',
    'Daman and Diu',
  ],
  Delhi: ['NCT of Delhi'],
  Puducherry: ['Pondicherry'],
  Uttarakhand: ['Uttaranchal'],
};
